import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  getHeroContent, StoreLogo, CartBtn, TabContent, StoreFooter, AWS_URL, FaHeart, InlineEditableImage
} from './ThemeLayoutHelpers';

// Custom InlineEditableTextWithNav component for proper editing and saving
const InlineEditableTextWithNav = ({ p, field, value, className = "", style = {}, children, onNavigate, navPage, isInModal = false }) => {
  const isEditing = p.previewEditMode && p.activeEditField === field;
  const isEditable = typeof p.onActivateField === "function";
  
  // Get the current value from storeTheme first, then from props
  const getCurrentValue = () => {
    if (p.storeTheme && p.storeTheme[field] !== undefined && p.storeTheme[field] !== "") {
      return p.storeTheme[field];
    }
    // Also check p.themeText if available (for preview mode)
    if (p.themeText && p.themeText[field] !== undefined && p.themeText[field] !== "") {
      return p.themeText[field];
    }
    return value;
  };
  
  const [localValue, setLocalValue] = useState(getCurrentValue());

  // Update local value when storeTheme changes or when editing starts
  useEffect(() => {
    if (!isEditing) {
      setLocalValue(getCurrentValue());
    }
  }, [p.storeTheme, field, value, isEditing, p.themeText]);

  const handleActivate = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const currentVal = getCurrentValue();
    setLocalValue(currentVal);
    if (typeof p.onActivateField === "function") {
      p.onActivateField(field);
    }
  };

  const handleChange = (event) => {
    const newValue = event.target.value;
    setLocalValue(newValue);
  };

  const handleSaveAndClose = () => {
    // Save the current local value to parent
    if (typeof p.onPreviewTextChange === "function") {
      p.onPreviewTextChange(field, localValue);
    }
    // Deactivate edit mode
    if (typeof p.onDeactivateField === "function") {
      p.onDeactivateField();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      handleSaveAndClose();
      // Then navigate if provided and not in modal
      if (!isInModal && onNavigate && navPage) {
        onNavigate(navPage);
      }
    }
  };

  const handleBlur = () => {
    // Save on blur (when clicking outside)
    handleSaveAndClose();
  };

  if (isEditing) {
    const inputClass = "w-full rounded-2xl border border-slate-200 bg-white/90 px-3 py-2 text-sm text-slate-900 outline-none focus:border-[#d4af37] focus:ring-2 focus:ring-[#d4af37]/20 dark:border-slate-700 dark:bg-gray-800 dark:text-gray-100";
    const multiline = field.toLowerCase().includes("subtitle") || field.toLowerCase().includes("description") || field.toLowerCase().includes("overview") || field.toLowerCase().includes("content") || field.toLowerCase().includes("fullstory") || field.toLowerCase().includes("story");

    return multiline ? (
      <textarea
        autoFocus
        rows={4}
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
        className={inputClass}
        style={{ whiteSpace: "pre-wrap", ...style }}
      />
    ) : (
      <input
        autoFocus
        type="text"
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
        className={inputClass}
        style={{ whiteSpace: "pre-wrap", ...style }}
      />
    );
  }

  // Display the current value - force re-render when storeTheme changes
  const displayValue = p.storeTheme?.[field] || p.themeText?.[field] || value;
  
  return (
    <span
      className={`${className} ${isEditable ? 'cursor-pointer inline-block' : 'inline-block'}`}
      style={{ whiteSpace: "pre-wrap", ...style }}
      onClick={isEditable ? handleActivate : undefined}
      title={p.previewEditMode ? "Edit text" : undefined}
    >
      {displayValue}
    </span>
  );
};

export default function VogueLayout({ p }) {
  const T = p.storeTheme || {};
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [visibleCards, setVisibleCards] = useState([]);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [modelAnimationComplete, setModelAnimationComplete] = useState(false);
  const [activePage, setActivePage] = useState("home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [selectedShoe, setSelectedShoe] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  
  // Force re-render when storeTheme changes
  const [refreshKey, setRefreshKey] = useState(0);
  
  useEffect(() => {
    // This will trigger re-render when storeTheme updates
    setRefreshKey(prev => prev + 1);
  }, [p.storeTheme]);
  
  const products = (p.filteredProducts || []).slice(0, 8);
  
  // Shoes collection data with editable fields - NOW WITH imageField
  const shoesCollection = [
    {
      id: 1,
      nameKey: "shoe1_name",
      name: "Crystal Stiletto Heels",
      priceKey: "shoe1_price",
      price: 12999,
      originalPriceKey: "shoe1_original_price",
      originalPrice: 18999,
      imageField: "shoe1_image",
      image: "https://images.unsplash.com/photo-1543163521-1bf539c55dd2?w=600&q=80",
      categoryKey: "shoe1_category",
      category: "Heels",
      descriptionKey: "shoe1_description",
      description: "Exquisite crystal-embellished stiletto heels perfect for red carpet events.",
      discountKey: "shoe1_discount",
      discount: 32
    },
    {
      id: 2,
      nameKey: "shoe2_name",
      name: "Pearl Embellished Pumps",
      priceKey: "shoe2_price",
      price: 9999,
      originalPriceKey: "shoe2_original_price",
      originalPrice: 14999,
      imageField: "shoe2_image",
      image: "https://images.unsplash.com/photo-1533867617858-e7b97e060509?w=600&q=80",
      categoryKey: "shoe2_category",
      category: "Pumps",
      descriptionKey: "shoe2_description",
      description: "Elegant pearl-adorned pumps for the sophisticated woman.",
      discountKey: "shoe2_discount",
      discount: 33
    },
    {
      id: 3,
      nameKey: "shoe3_name",
      name: "Gold Metallic Sandals",
      priceKey: "shoe3_price",
      price: 8999,
      originalPriceKey: "shoe3_original_price",
      originalPrice: 12999,
      imageField: "shoe3_image",
      image: "https://images.unsplash.com/photo-1591360234995-40b7caaa9620?w=600&q=80",
      categoryKey: "shoe3_category",
      category: "Sandals",
      descriptionKey: "shoe3_description",
      description: "Stunning gold metallic sandals that catch every eye.",
      discountKey: "shoe3_discount",
      discount: 31
    },
    {
      id: 4,
      nameKey: "shoe4_name",
      name: "Lace-Up Boots",
      priceKey: "shoe4_price",
      price: 15999,
      originalPriceKey: "shoe4_original_price",
      originalPrice: 22999,
      imageField: "shoe4_image",
      image: "https://images.unsplash.com/photo-1542840714-2e8c4b3c7c8b?w=600&q=80",
      categoryKey: "shoe4_category",
      category: "Boots",
      descriptionKey: "shoe4_description",
      description: "Edgy lace-up boots with a modern twist.",
      discountKey: "shoe4_discount",
      discount: 30
    },
    {
      id: 5,
      nameKey: "shoe5_name",
      name: "Diamond Ankle Strap Heels",
      priceKey: "shoe5_price",
      price: 18999,
      originalPriceKey: "shoe5_original_price",
      originalPrice: 26999,
      imageField: "shoe5_image",
      image: "https://images.unsplash.com/photo-1543163521-1bf539c55dd2?w=600&q=80",
      categoryKey: "shoe5_category",
      category: "Heels",
      descriptionKey: "shoe5_description",
      description: "Luxury diamond-encrusted ankle strap heels.",
      discountKey: "shoe5_discount",
      discount: 30
    },
    {
      id: 6,
      nameKey: "shoe6_name",
      name: "Silk Satin Mules",
      priceKey: "shoe6_price",
      price: 7999,
      originalPriceKey: "shoe6_original_price",
      originalPrice: 11999,
      imageField: "shoe6_image",
      image: "https://images.unsplash.com/photo-1591360234995-40b7caaa9620?w=600&q=80",
      categoryKey: "shoe6_category",
      category: "Mules",
      descriptionKey: "shoe6_description",
      description: "Buttery soft silk satin mules for effortless elegance.",
      discountKey: "shoe6_discount",
      discount: 33
    },
    {
      id: 7,
      nameKey: "shoe7_name",
      name: "Embellished Wedges",
      priceKey: "shoe7_price",
      price: 10999,
      originalPriceKey: "shoe7_original_price",
      originalPrice: 15999,
      imageField: "shoe7_image",
      image: "https://images.unsplash.com/photo-1533867617858-e7b97e060509?w=600&q=80",
      categoryKey: "shoe7_category",
      category: "Wedges",
      descriptionKey: "shoe7_description",
      description: "Comfortable wedges with statement embellishments.",
      discountKey: "shoe7_discount",
      discount: 31
    },
    {
      id: 8,
      nameKey: "shoe8_name",
      name: "Pearl Strap Heels",
      priceKey: "shoe8_price",
      price: 13999,
      originalPriceKey: "shoe8_original_price",
      originalPrice: 19999,
      imageField: "shoe8_image",
      image: "https://images.unsplash.com/photo-1543163521-1bf539c55dd2?w=600&q=80",
      categoryKey: "shoe8_category",
      category: "Heels",
      descriptionKey: "shoe8_description",
      description: "Elegant pearl strap heels for special occasions.",
      discountKey: "shoe8_discount",
      discount: 30
    }
  ];

  // Get editable shoes with imageField
  const editableShoes = shoesCollection.map((shoe) => ({
    ...shoe,
    image: p.storeTheme?.[shoe.imageField] || shoe.image,
  }));
  
  // Blog posts data with editable fields - NOW WITH imageField
  const editorialPosts = [
    {
      id: 1,
      titleKey: "post1_title",
      title: "The Art of Couture",
      descKey: "post1_desc",
      desc: "Discover the meticulous craftsmanship behind our latest collection.",
      fullContentKey: "post1_content",
      fullContent: "Couture is not just clothing—it's wearable art. Our atelier has been perfecting the craft of haute couture for decades. Every stitch, every seam, every detail is carefully considered to create pieces that transcend time. From the initial sketch to the final fitting, each garment undergoes a transformative journey that honors the rich heritage of fashion while embracing modern innovation.",
      imageField: "post1_image",
      image: "https://images.unsplash.com/photo-1539109136881-3be0616acf4b?w=800&q=80",
      dateKey: "post1_date",
      date: "March 15, 2024",
      authorKey: "post1_author",
      author: "Isabella Rossi"
    },
    {
      id: 2,
      titleKey: "post2_title",
      title: "Sustainable Luxury",
      descKey: "post2_desc",
      desc: "How we're redefining luxury with ethical practices.",
      fullContentKey: "post2_content",
      fullContent: "True luxury respects both the wearer and the planet. Our commitment to sustainability is unwavering. We source materials responsibly, minimize waste in our production process, and ensure fair wages for all our artisans. Luxury shouldn't come at the cost of our planet—it should be a force for good.",
      imageField: "post2_image",
      image: "https://images.unsplash.com/photo-1483985988355-763728e1935b?w=800&q=80",
      dateKey: "post2_date",
      date: "March 10, 2024",
      authorKey: "post2_author",
      author: "Marcus Chen"
    },
    {
      id: 3,
      titleKey: "post3_title",
      title: "Behind the Collection",
      descKey: "post3_desc",
      desc: "An exclusive look at our design process.",
      fullContentKey: "post3_content",
      fullContent: "Every collection begins with a spark of inspiration. Join us on a journey through our creative process, from mood boards to fabric selection, from pattern making to the final runway show. This is where fashion comes alive—where dreams take shape and beauty is born.",
      imageField: "post3_image",
      image: "https://images.unsplash.com/photo-1469334031218-e382a71b716b?w=800&q=80",
      dateKey: "post3_date",
      date: "March 5, 2024",
      authorKey: "post3_author",
      author: "Elena Vasquez"
    }
  ];

  // Get editable editorial posts with imageField
  const editableEditorialPosts = editorialPosts.map((post) => ({
    ...post,
    image: p.storeTheme?.[post.imageField] || post.image,
  }));
  
  // Image configs
  const heroImageConfig = {
    field: "heroImage",
    src: "https://images.pexels.com/photos/1456706/pexels-photo-1456706.jpeg?auto=compress&cs=tinysrgb&w=1600",
  };
  const backupImages = ["https://images.pexels.com/photos/1183266/pexels-photo-1183266.jpeg?auto=compress&cs=tinysrgb&w=1600"];
  
  const coutureImageConfig = {
    field: "coutureImage",
    src: "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=800&q=80",
  };
  
  const aboutImageConfig = {
    field: "aboutImage",
    src: "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=800&q=80",
  };
  
  // Navigate to page - normal navigation
  const navigateTo = useCallback((page, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    // Don't navigate if we're actively editing
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setActivePage(page);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [p.previewEditMode, p.activeEditField]);
  
  // Handle navigation after Enter key in edit mode
  const handleNavigateAfterEdit = useCallback((page) => {
    setActivePage(page);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  
  // Open blog modal
  const openBlogModal = useCallback((post, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setSelectedPost(post);
  }, [p.previewEditMode, p.activeEditField]);
  
  // Close blog modal
  const closeBlogModal = useCallback(() => {
    setSelectedPost(null);
  }, []);
  
  // Handle backdrop click
  const handleBackdropClick = useCallback((event) => {
    if (event.target === event.currentTarget) {
      if (p.previewEditMode && p.activeEditField) {
        return;
      }
      setSelectedPost(null);
    }
  }, [p.previewEditMode, p.activeEditField]);
  
  // Scroll handler
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  
  // Full screen model image
  const modelImage = p.storeTheme?.heroImage || heroImageConfig.src;
  
  // Animate model image in
  useEffect(() => {
    const img = new Image();
    img.src = modelImage;
    img.onload = () => { setTimeout(() => { setModelAnimationComplete(true); }, 100); };
    img.onerror = () => {
      const backupImg = new Image();
      backupImg.src = backupImages[0];
      backupImg.onload = () => { setTimeout(() => { setModelAnimationComplete(true); }, 100); };
    };
  }, [modelImage]);
  
  // Staggered animation for product cards
  useEffect(() => {
    if (modelAnimationComplete) {
      products.forEach((_, index) => {
        setTimeout(() => { setVisibleCards(prev => [...prev, index]); }, index * 120);
      });
    }
  }, [modelAnimationComplete, products]);
  
  const copy = getHeroContent(p, {
    eyebrow: "VOGUE COUTURE",
    title: "Elegance Redefined",
    subtitle: "Experience the pinnacle of designer fashion with our exclusive collection",
    primaryCta: "Shop Collection",
    secondaryCta: "View Shoes",
  });
  
  // Different entrance directions for cards
  const getCardEntrance = (index) => {
    const directions = [
      { x: -150, y: 0, rotate: -12, scale: 0.7 },
      { x: 150, y: 0, rotate: 12, scale: 0.7 },
      { x: 0, y: -120, rotate: -8, scale: 0.7 },
      { x: 0, y: 120, rotate: 8, scale: 0.7 },
      { x: -120, y: -80, rotate: -15, scale: 0.65 },
      { x: 120, y: -80, rotate: 15, scale: 0.65 },
      { x: -120, y: 80, rotate: -10, scale: 0.65 },
      { x: 120, y: 80, rotate: 10, scale: 0.65 },
    ];
    return directions[index % directions.length];
  };

  // Top bar messages
  const topBarMessages = [
    { key: "top_bar_message_1", value: "VOGUE COUTURE S/S 2024" },
    { key: "top_bar_message_2", value: "COMPLIMENTARY GIFT BOX" },
    { key: "top_bar_message_3", value: "FREE SHIPPING WORLDWIDE" },
    { key: "top_bar_message_4", value: "EXCLUSIVE DESIGNER DROP" },
  ];

  // Navigation items
  const navItems = [
    { key: "nav_home", label: "HOME", page: "home" },
    { key: "nav_couture", label: "COUTURE", page: "couture" },
    { key: "nav_collection", label: "COLLECTION", page: "collection" },
    { key: "nav_shoes", label: "SHOES", page: "shoes" },
    { key: "nav_editorial", label: "EDITORIAL", page: "editorial" },
    { key: "nav_about", label: "ABOUT", page: "about" },
  ];

  // Category items
  const categoryItems = [
    { key: "cat_evening_gowns", name: "Evening Gowns", icon: "👗", page: "collection" },
    { key: "cat_couture_dresses", name: "Couture Dresses", icon: "✨", page: "collection" },
    { key: "cat_luxury_shoes", name: "Luxury Shoes", icon: "👠", page: "shoes" },
    { key: "cat_fine_jewelry", name: "Fine Jewelry", icon: "💎", page: "collection" }
  ];

  // Shoe category filters
  const shoeCategories = ["All", "Heels", "Pumps", "Sandals", "Boots", "Mules", "Wedges"];

  // Tab items
  const tabItems = [
    { key: "products", labelKey: "tab_products_label", label: "COLLECTION" },
    { key: "services", labelKey: "tab_services_label", label: "SERVICES" },
    { key: "details", labelKey: "tab_details_label", label: "EDITORIAL" },
  ];

  // About stats
  const aboutStats = [
    { valueKey: "about_stat1_value", value: "200+", labelKey: "about_stat1_label", label: "Hours per gown" },
    { valueKey: "about_stat2_value", value: "50+", labelKey: "about_stat2_label", label: "Artisans" },
    { valueKey: "about_stat3_value", value: "15+", labelKey: "about_stat3_label", label: "Countries" },
  ];
  
  return (
    <div key={refreshKey} className="min-h-screen bg-[#faf8f5]" style={{ fontFamily: T.fontFamily || "'Playfair Display', serif" }}>
      
      {/* Top Bar - Light Theme - All messages editable */}
      <div className="relative bg-[#1a1a2e] text-[#d4af37] py-2 text-center text-xs tracking-wider overflow-hidden z-20">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#d4af37]/5 to-transparent animate-shimmer-slow pointer-events-none" />
        <div className="animate-marquee whitespace-nowrap">
          {topBarMessages.map((msg, index) => (
            <span key={msg.key} className="inline-block mx-8">
              ✦ <InlineEditableTextWithNav 
                p={p} 
                field={msg.key} 
                value={msg.value} 
                onNavigate={handleNavigateAfterEdit} 
                navPage="home" 
              /> ✦
            </span>
          ))}
        </div>
      </div>
      
      {/* Navigation - Light Theme */}
      <nav className={`sticky top-0 z-50 transition-all duration-500 ${isScrolled ? "bg-white/95 backdrop-blur-md shadow-md py-3" : "bg-white/80 backdrop-blur-sm py-4"}`}>
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={(e) => navigateTo("home", e)}>
            <StoreLogo storeInfo={p.storeInfo} border="border-2 border-[#d4af37]" />
            <span className="font-bold text-xl tracking-tight text-[#1a1a2e]">
              <InlineEditableTextWithNav 
                p={p} 
                field="businessName" 
                value={p.storeInfo?.businessName || "Vogue Couture"} 
                onNavigate={handleNavigateAfterEdit} 
                navPage="home" 
              />
            </span>
          </div>
          
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2">
            <svg className="w-6 h-6 text-[#1a1a2e]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          
          <div className="hidden md:flex gap-10">
            {navItems.map((item) => (
              <button 
                key={item.key} 
                onClick={(e) => navigateTo(item.page, e)} 
                className={`text-sm transition-all duration-300 relative group ${activePage === item.page ? "text-[#d4af37]" : "text-gray-600 hover:text-[#d4af37]"}`}
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field={item.key} 
                  value={item.label} 
                  onNavigate={handleNavigateAfterEdit} 
                  navPage={item.page} 
                />
                <span className={`absolute -bottom-1 left-0 w-0 h-px bg-[#d4af37] transition-all duration-300 ${activePage === item.page ? "w-full" : "group-hover:w-full"}`} />
              </button>
            ))}
          </div>
          
          <div className="flex gap-5">
            <button onClick={p.handleGoToWishlist} className="relative group">
              <FaHeart className="text-gray-600 text-xl group-hover:text-[#d4af37] transition-colors" />
              {p.wishlistCount > 0 && (
                <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-[#d4af37] text-white text-[10px] flex items-center justify-center font-bold animate-pulse">
                  {p.wishlistCount}
                </span>
              )}
            </button>
            <CartBtn p={p} />
            {p.userMenuElement}
          </div>
        </div>
        
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-white shadow-lg border-t border-[#d4af37]/20 py-4 px-4 flex flex-col gap-2 z-50">
            {navItems.map((item) => (
              <button 
                key={item.key} 
                onClick={(e) => navigateTo(item.page, e)} 
                className={`text-sm py-2 px-3 rounded-lg text-left ${activePage === item.page ? "bg-[#d4af37]/10 text-[#d4af37]" : "text-gray-700"}`}
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field={item.key} 
                  value={item.label} 
                  onNavigate={handleNavigateAfterEdit} 
                  navPage={item.page} 
                />
              </button>
            ))}
          </div>
        )}
      </nav>
      
      {/* ==================== HOME PAGE - FIXED GAP ==================== */}
      {activePage === "home" && (
        <section className="relative min-h-screen overflow-hidden">
          <div className="w-full h-screen">
            <div className={`w-full h-full transition-all duration-1000 ease-out ${modelAnimationComplete ? "opacity-100 scale-100" : "opacity-0 scale-105"}`} style={{ transitionTimingFunction: "cubic-bezier(0.34, 1.2, 0.64, 1)" }}>
              {/* Editable Hero Image */}
              <InlineEditableImage p={p} field={heroImageConfig.field} className="block h-full w-full overflow-hidden">
                <img 
                  src={modelImage} 
                  alt="Designer Dress Model" 
                  className="w-full h-full object-cover object-top" 
                />
              </InlineEditableImage>
              <div className="absolute inset-0 bg-gradient-to-r from-white/80 via-white/40 to-transparent pointer-events-none" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#faf8f5] via-transparent to-transparent pointer-events-none" />
            </div>
            
            {/* Floating Accent Elements */}
            <div className="absolute top-1/4 left-10 animate-float"><div className="w-16 h-16 border border-[#d4af37]/30 rounded-full flex items-center justify-center bg-white/20 backdrop-blur-sm"><div className="w-2 h-2 bg-[#d4af37] rounded-full animate-ping" /></div></div>
            <div className="absolute bottom-1/3 right-10 animate-float-delayed"><div className="w-24 h-24 border border-[#d4af37]/20 rounded-full flex items-center justify-center bg-white/20 backdrop-blur-sm">
              <span className="text-[#d4af37] text-xs tracking-wider">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="floating_couture_text" 
                  value="COUTURE" 
                  onNavigate={handleNavigateAfterEdit} 
                  navPage="home" 
                />
              </span>
            </div></div>
            
            <div className="absolute inset-0 flex items-center">
              <div className="max-w-7xl mx-auto px-4 w-full">
                <div className="max-w-2xl">
                  <div className="inline-block px-3 py-1 bg-[#d4af37]/10 backdrop-blur-sm border-l-2 border-[#d4af37] mb-4 animate-fade-in">
                    <span className="text-[#d4af37] text-xs tracking-[0.2em] uppercase font-light">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field="heroEyebrow" 
                        value={copy.eyebrow} 
                        onNavigate={handleNavigateAfterEdit} 
                        navPage="home" 
                      />
                    </span>
                  </div>
                  <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-[#1a1a2e] leading-[1.1] tracking-tight">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="heroTitle" 
                      value={copy.title} 
                      onNavigate={handleNavigateAfterEdit} 
                      navPage="home" 
                    />
                  </h1>
                  <p className="mt-6 text-gray-600 text-lg max-w-md font-light animate-fade-in delay-200">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="heroSubtitle" 
                      value={copy.subtitle} 
                      onNavigate={handleNavigateAfterEdit} 
                      navPage="home" 
                    />
                  </p>
                  <div className="mt-10 flex gap-4 animate-fade-in delay-300">
                    <button onClick={(e) => navigateTo("collection", e)} className="group px-8 py-4 rounded-full bg-[#d4af37] text-white font-semibold tracking-wide transition-all duration-300 hover:scale-105 hover:shadow-2xl flex items-center gap-2">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field="primaryCtaText" 
                        value={copy.primaryCta} 
                        onNavigate={handleNavigateAfterEdit} 
                        navPage="collection" 
                      />
                      <span className="group-hover:translate-x-1 transition-transform">→</span>
                    </button>
                    <button onClick={(e) => navigateTo("shoes", e)} className="px-8 py-4 rounded-full border-2 border-[#d4af37] text-[#d4af37] font-semibold tracking-wide transition-all duration-300 hover:bg-[#d4af37] hover:text-white backdrop-blur-sm">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field="secondaryCtaText" 
                        value={copy.secondaryCta} 
                        onNavigate={handleNavigateAfterEdit} 
                        navPage="shoes" 
                      />
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce-slow">
              <div className="flex flex-col items-center gap-1">
                <span className="text-gray-400 text-[10px] tracking-wider">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="scroll_text" 
                    value="SCROLL" 
                    onNavigate={handleNavigateAfterEdit} 
                    navPage="home" 
                  />
                </span>
                <div className="w-5 h-8 border border-gray-300 rounded-full flex justify-center">
                  <div className="w-0.5 h-2 bg-[#d4af37] rounded-full mt-1 animate-scroll" />
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
      
      {/* Content Section - FIXED: Now starts immediately after home section with no gap */}
      <div className={`relative z-10 ${activePage === "home" ? "mt-0" : ""} bg-[#faf8f5] rounded-t-3xl shadow-xl`}>
        
        {modelAnimationComplete && activePage === "home" && (
          <div className="text-center pt-8 pb-4 animate-bounce-in">
            <div className="inline-flex items-center gap-2 px-5 py-2 bg-[#d4af37] rounded-full">
              <span className="text-white text-sm">✦</span>
              <span className="text-white text-xs tracking-wider">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="collection_badge" 
                  value="COUTURE COLLECTION ARRIVED" 
                  onNavigate={handleNavigateAfterEdit} 
                  navPage="collection" 
                />
              </span>
              <span className="text-white text-sm">✦</span>
            </div>
          </div>
        )}
        
        {/* ==================== COLLECTION PAGE ==================== */}
        {activePage === "collection" && (
          <section className="max-w-7xl mx-auto px-4 py-12">
            <div className="text-center mb-10">
              <span className="text-[#d4af37] text-sm tracking-[0.2em] uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="collection_badge_text" 
                  value="The Collection" 
                  onNavigate={handleNavigateAfterEdit} 
                  navPage="collection" 
                />
              </span>
              <h2 className="text-3xl md:text-4xl font-light text-[#1a1a2e] mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="collection_title" 
                  value="Featured Pieces" 
                  onNavigate={handleNavigateAfterEdit} 
                  navPage="collection" 
                />
              </h2>
              <div className="w-20 h-px bg-gradient-to-r from-transparent via-[#d4af37] to-transparent mx-auto mt-3" />
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {products.map((product, idx) => {
                const entrance = getCardEntrance(idx);
                const isVisible = visibleCards.includes(idx);
                return (
                  <div 
                    key={product._id} 
                    className={`group cursor-pointer transition-all duration-600 ${isVisible ? "opacity-100 translate-x-0 translate-y-0 rotate-0 scale-100" : `opacity-0 translate-x-[${entrance.x}px] translate-y-[${entrance.y}px] rotate-${Math.abs(entrance.rotate)} scale-${entrance.scale * 100}`}`} 
                    style={{ transitionTimingFunction: "cubic-bezier(0.34, 1.2, 0.64, 1)" }} 
                    onMouseEnter={() => setHoveredCard(idx)} 
                    onMouseLeave={() => setHoveredCard(null)} 
                    onClick={() => setSelectedProduct(product)}
                  >
                    <div className="relative overflow-hidden rounded-xl bg-white shadow-md hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
                      <div className="relative aspect-[3/4] overflow-hidden bg-gray-50">
                        {product.productImage ? (
                          <img src={AWS_URL + product.productImage} alt={product.productName} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-5xl bg-gradient-to-br from-gray-50 to-gray-100">👗</div>
                        )}
                        {/* View Details Overlay - NO ADD TO CART */}
                        <div className={`absolute inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center transition-all duration-400 ${hoveredCard === idx ? "opacity-100" : "opacity-0"}`}>
                          <button 
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              setSelectedProduct(product); 
                            }} 
                            className="px-5 py-2 bg-[#d4af37] text-white rounded-full text-sm font-semibold transform transition-all duration-300 hover:scale-105"
                          >
                            <InlineEditableTextWithNav 
                              p={p} 
                              field="quick_view_button" 
                              value="View Details" 
                              onNavigate={handleNavigateAfterEdit} 
                              navPage="collection" 
                            />
                          </button>
                        </div>
                        {product.discount > 0 && (
                          <div className="absolute top-3 left-3 bg-[#d4af37] text-white text-xs font-bold px-2 py-1 rounded-full">
                            <InlineEditableTextWithNav 
                              p={p} 
                              field="sale_badge" 
                              value="SALE" 
                              onNavigate={handleNavigateAfterEdit} 
                              navPage="collection" 
                            />
                          </div>
                        )}
                      </div>
                      <div className="p-4 text-center">
                        <h3 className="font-medium text-gray-800 text-sm line-clamp-1">{product.productName}</h3>
                        <div className="mt-2 flex items-center justify-center gap-2">
                          <span className="text-xl font-bold text-[#d4af37]">₹{product.salePrice}</span>
                          {product.mrp > product.salePrice && (
                            <span className="text-sm text-gray-400 line-through">₹{product.mrp}</span>
                          )}
                        </div>
                        <div className="mt-2 flex items-center justify-center gap-0.5">
                          {[...Array(5)].map((_, i) => (
                            <span key={i} className="text-[#d4af37] text-xs">★</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {visibleCards.length === products.length && products.length > 0 && (
              <div className="text-center mt-12 animate-fade-in">
                <button onClick={() => p.setActiveTab("products")} className="group px-8 py-3 rounded-full bg-[#1a1a2e] text-white font-semibold transition-all duration-300 hover:bg-[#d4af37] hover:text-white inline-flex items-center gap-2">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="explore_full_collection" 
                    value="Explore Full Collection" 
                    onNavigate={handleNavigateAfterEdit} 
                    navPage="collection" 
                  />
                  <span className="group-hover:translate-x-1 transition-transform">→</span>
                </button>
              </div>
            )}
          </section>
        )}
        
        {/* ==================== SHOES COLLECTION PAGE ==================== */}
        {activePage === "shoes" && (
          <section className="max-w-7xl mx-auto px-4 py-12">
            <div className="text-center mb-10">
              <span className="text-[#d4af37] text-sm tracking-[0.2em] uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="shoes_badge" 
                  value="Footwear Collection" 
                  onNavigate={handleNavigateAfterEdit} 
                  navPage="shoes" 
                />
              </span>
              <h2 className="text-3xl md:text-4xl font-light text-[#1a1a2e] mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="shoes_title" 
                  value="Luxury Shoes" 
                  onNavigate={handleNavigateAfterEdit} 
                  navPage="shoes" 
                />
              </h2>
              <div className="w-20 h-px bg-gradient-to-r from-transparent via-[#d4af37] to-transparent mx-auto mt-3" />
              <p className="text-gray-500 text-sm mt-4 max-w-2xl mx-auto">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="shoes_subtitle" 
                  value="Step into elegance with our curated collection of designer footwear" 
                  onNavigate={handleNavigateAfterEdit} 
                  navPage="shoes" 
                />
              </p>
            </div>
            
            {/* Category Filters */}
            <div className="flex flex-wrap justify-center gap-3 mb-10">
              {shoeCategories.map((cat) => (
                <button key={cat} className="px-4 py-1.5 rounded-full text-sm border border-[#d4af37]/30 text-gray-600 hover:bg-[#d4af37] hover:text-white transition-all duration-300">
                  {cat}
                </button>
              ))}
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {editableShoes.map((shoe, idx) => (
                <div 
                  key={shoe.id} 
                  className="group cursor-pointer animate-fade-in" 
                  style={{ animationDelay: `${idx * 0.1}s` }} 
                  onClick={() => setSelectedShoe(shoe)}
                >
                  <div className="relative overflow-hidden rounded-xl bg-white shadow-md hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
                    <div className="relative aspect-square overflow-hidden bg-gray-50">
                      <InlineEditableImage p={p} field={shoe.imageField} className="block w-full h-full overflow-hidden">
                        <img src={shoe.image} alt={shoe.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                      </InlineEditableImage>
                      {/* View Details Overlay - NO ADD TO CART */}
                      <div className="absolute inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center transition-all duration-400 opacity-0 group-hover:opacity-100">
                        <button 
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            setSelectedShoe(shoe); 
                          }} 
                          className="px-5 py-2 bg-[#d4af37] text-white rounded-full text-sm font-semibold transform transition-all duration-300 hover:scale-105"
                        >
                          <InlineEditableTextWithNav 
                            p={p} 
                            field="quick_view_button" 
                            value="View Details" 
                            onNavigate={handleNavigateAfterEdit} 
                            navPage="shoes" 
                          />
                        </button>
                      </div>
                      {shoe.discount > 0 && (
                        <div className="absolute top-3 left-3 bg-[#d4af37] text-white text-xs font-bold px-2 py-1 rounded-full">
                          <InlineEditableTextWithNav 
                            p={p} 
                            field={shoe.discountKey} 
                            value={`-${shoe.discount}%`} 
                            onNavigate={handleNavigateAfterEdit} 
                            navPage="shoes" 
                          />
                        </div>
                      )}
                      <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full text-xs font-semibold text-[#d4af37]">
                        <InlineEditableTextWithNav 
                          p={p} 
                          field={shoe.categoryKey} 
                          value={shoe.category} 
                          onNavigate={handleNavigateAfterEdit} 
                          navPage="shoes" 
                        />
                      </div>
                    </div>
                    <div className="p-4 text-center">
                      <h3 className="font-medium text-gray-800 text-sm line-clamp-1">
                        <InlineEditableTextWithNav 
                          p={p} 
                          field={shoe.nameKey} 
                          value={shoe.name} 
                          onNavigate={handleNavigateAfterEdit} 
                          navPage="shoes" 
                        />
                      </h3>
                      <div className="mt-2 flex items-center justify-center gap-2">
                        <span className="text-xl font-bold text-[#d4af37]">
                          ₹<InlineEditableTextWithNav 
                            p={p} 
                            field={shoe.priceKey} 
                            value={shoe.price.toString()} 
                            onNavigate={handleNavigateAfterEdit} 
                            navPage="shoes" 
                          />
                        </span>
                        <span className="text-sm text-gray-400 line-through">
                          ₹<InlineEditableTextWithNav 
                            p={p} 
                            field={shoe.originalPriceKey} 
                            value={shoe.originalPrice.toString()} 
                            onNavigate={handleNavigateAfterEdit} 
                            navPage="shoes" 
                          />
                        </span>
                      </div>
                      <div className="mt-2 flex items-center justify-center gap-0.5">
                        {[...Array(5)].map((_, i) => (
                          <span key={i} className="text-[#d4af37] text-xs">★</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
        
        {/* ==================== COUTURE PAGE ==================== */}
        {activePage === "couture" && (
          <section className="max-w-7xl mx-auto px-4 py-20">
            <div className="text-center mb-12">
              <span className="text-[#d4af37] text-sm tracking-[0.2em] uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="couture_badge" 
                  value="Haute Couture" 
                  onNavigate={handleNavigateAfterEdit} 
                  navPage="couture" 
                />
              </span>
              <h2 className="text-4xl md:text-5xl font-light text-[#1a1a2e] mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="couture_title" 
                  value="The Art of Couture" 
                  onNavigate={handleNavigateAfterEdit} 
                  navPage="couture" 
                />
              </h2>
              <div className="w-20 h-px bg-gradient-to-r from-transparent via-[#d4af37] to-transparent mx-auto mt-4" />
            </div>
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <p className="text-gray-600 leading-relaxed mb-4">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="couture_para1" 
                    value="Haute couture represents the pinnacle of fashion artistry. Each piece is a masterpiece, meticulously handcrafted by our master artisans using the finest materials from around the world." 
                    onNavigate={handleNavigateAfterEdit} 
                    navPage="couture" 
                  />
                </p>
                <p className="text-gray-600 leading-relaxed">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="couture_para2" 
                    value="From initial sketch to final fitting, every gown requires over 200 hours of dedicated work. This is the essence of true luxury—wearable art that tells a story." 
                    onNavigate={handleNavigateAfterEdit} 
                    navPage="couture" 
                  />
                </p>
                <button onClick={(e) => navigateTo("collection", e)} className="mt-6 px-8 py-3 rounded-full bg-[#d4af37] text-white font-semibold hover:bg-[#1a1a2e] transition-all duration-300">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="explore_collection_button" 
                    value="Explore Collection" 
                    onNavigate={handleNavigateAfterEdit} 
                    navPage="collection" 
                  />
                </button>
              </div>
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <InlineEditableImage p={p} field={coutureImageConfig.field} className="block w-full overflow-hidden">
                  <img 
                    src={p.storeTheme?.coutureImage || coutureImageConfig.src} 
                    alt="Couture" 
                    className="w-full h-96 object-cover" 
                  />
                </InlineEditableImage>
              </div>
            </div>
          </section>
        )}
        
        {/* ==================== EDITORIAL PAGE ==================== */}
        {activePage === "editorial" && (
          <section className="max-w-7xl mx-auto px-4 py-12">
            <div className="text-center mb-12">
              <span className="text-[#d4af37] text-sm tracking-[0.2em] uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="editorial_badge" 
                  value="Editorial" 
                  onNavigate={handleNavigateAfterEdit} 
                  navPage="editorial" 
                />
              </span>
              <h2 className="text-4xl md:text-5xl font-light text-[#1a1a2e] mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="editorial_title" 
                  value="Latest Stories" 
                  onNavigate={handleNavigateAfterEdit} 
                  navPage="editorial" 
                />
              </h2>
              <div className="w-20 h-px bg-gradient-to-r from-transparent via-[#d4af37] to-transparent mx-auto mt-4" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {editableEditorialPosts.map((post) => (
                <div 
                  key={post.id} 
                  className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer"
                  onClick={(e) => openBlogModal(post, e)}
                >
                  <div className="h-56 overflow-hidden">
                    <InlineEditableImage p={p} field={post.imageField} className="block w-full h-full overflow-hidden">
                      <img src={post.image} alt={post.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    </InlineEditableImage>
                  </div>
                  <div className="p-5">
                    <p className="text-xs text-gray-400 mb-2">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={post.dateKey} 
                        value={post.date} 
                        onNavigate={handleNavigateAfterEdit} 
                        navPage="editorial" 
                        isInModal={true} 
                      />
                    </p>
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={post.titleKey} 
                        value={post.title} 
                        onNavigate={handleNavigateAfterEdit} 
                        navPage="editorial" 
                        isInModal={true} 
                      />
                    </h3>
                    <p className="text-gray-500 text-sm">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={post.descKey} 
                        value={post.desc} 
                        onNavigate={handleNavigateAfterEdit} 
                        navPage="editorial" 
                        isInModal={true} 
                      />
                    </p>
                    <button 
                      onClick={(e) => openBlogModal(post, e)} 
                      className="mt-4 text-[#d4af37] text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all"
                    >
                      <InlineEditableTextWithNav 
                        p={p} 
                        field="read_more_button" 
                        value="Read More" 
                        onNavigate={handleNavigateAfterEdit} 
                        navPage="editorial" 
                      />
                      <span>→</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
        
        {/* ==================== ABOUT PAGE ==================== */}
        {activePage === "about" && (
          <section className="max-w-7xl mx-auto px-4 py-20">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <span className="text-[#d4af37] text-sm tracking-[0.2em] uppercase">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="about_badge" 
                    value="Our Story" 
                    onNavigate={handleNavigateAfterEdit} 
                    navPage="about" 
                  />
                </span>
                <h2 className="text-4xl md:text-5xl font-light text-[#1a1a2e] mt-3">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="about_title" 
                    value="A Legacy of Elegance" 
                    onNavigate={handleNavigateAfterEdit} 
                    navPage="about" 
                  />
                </h2>
                <div className="w-16 h-px bg-[#d4af37] mt-4 mb-6" />
                <p className="text-gray-600 leading-relaxed mb-4">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="about_para1" 
                    value="Founded with a passion for timeless elegance, Vogue Couture represents the pinnacle of designer fashion. Each piece is meticulously crafted to perfection." 
                    onNavigate={handleNavigateAfterEdit} 
                    navPage="about" 
                  />
                </p>
                <p className="text-gray-600 leading-relaxed mb-6">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="about_para2" 
                    value="Our commitment to excellence extends beyond our products to every interaction with our brand. From personalized styling to luxurious packaging, we ensure every touchpoint reflects our dedication." 
                    onNavigate={handleNavigateAfterEdit} 
                    navPage="about" 
                  />
                </p>
                <div className="flex gap-6">
                  {aboutStats.map((stat) => (
                    <div key={stat.valueKey}>
                      <p className="text-3xl font-bold text-[#d4af37]">
                        <InlineEditableTextWithNav 
                          p={p} 
                          field={stat.valueKey} 
                          value={stat.value} 
                          onNavigate={handleNavigateAfterEdit} 
                          navPage="about" 
                        />
                      </p>
                      <p className="text-sm text-gray-500">
                        <InlineEditableTextWithNav 
                          p={p} 
                          field={stat.labelKey} 
                          value={stat.label} 
                          onNavigate={handleNavigateAfterEdit} 
                          navPage="about" 
                        />
                      </p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <InlineEditableImage p={p} field={aboutImageConfig.field} className="block w-full overflow-hidden">
                  <img 
                    src={p.storeTheme?.aboutImage || aboutImageConfig.src} 
                    alt="About" 
                    className="w-full h-full object-cover" 
                  />
                </InlineEditableImage>
              </div>
            </div>
          </section>
        )}
        
        {/* Categories - Only on home */}
        {activePage === "home" && (
          <div className="max-w-7xl mx-auto px-4 py-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {categoryItems.map((cat) => (
                <div 
                  key={cat.key} 
                  className="text-center p-4 rounded-xl bg-white shadow-sm hover:shadow-md transition-all cursor-pointer group" 
                  onClick={(e) => navigateTo(cat.page, e)}
                >
                  <div className="text-3xl mb-1 group-hover:scale-110 transition-transform">{cat.icon}</div>
                  <p className="text-sm text-gray-700 font-medium">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={cat.key} 
                      value={cat.name} 
                      onNavigate={handleNavigateAfterEdit} 
                      navPage={cat.page} 
                    />
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Tabs - Not on home */}
        {activePage !== "home" && (
          <div className="max-w-7xl mx-auto px-4 pt-8 pb-4">
            <div className="flex justify-center gap-2 flex-wrap border-t border-gray-200 pt-8">
              {tabItems.map((tab) => (
                <button 
                  key={tab.key} 
                  onClick={() => p.setActiveTab(tab.key)} 
                  className={`px-6 py-2 rounded-full text-sm transition-all duration-300 ${p.activeTab === tab.key ? "bg-[#d4af37] text-white" : "text-gray-500 hover:text-[#d4af37]"}`}
                >
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={tab.labelKey} 
                    value={tab.label} 
                    onNavigate={handleNavigateAfterEdit} 
                    navPage={activePage} 
                  />
                </button>
              ))}
            </div>
          </div>
        )}
        
        {/* Main Content */}
        <div className="flex-1 max-w-7xl mx-auto w-full px-4 py-6">
          <TabContent p={p} />
        </div>
        
        {/* Newsletter */}
        <div className="bg-gradient-to-r from-[#1a1a2e] to-[#2a2a4e] py-12 mt-8 rounded-t-3xl">
          <div className="max-w-3xl mx-auto text-center px-4">
            <span className="text-[#d4af37] text-xs tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="newsletter_badge" 
                value="Subscribe" 
                onNavigate={handleNavigateAfterEdit} 
                navPage="home" 
              />
            </span>
            <h3 className="text-2xl font-light text-white mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="newsletter_title" 
                value="Join the Couture Circle" 
                onNavigate={handleNavigateAfterEdit} 
                navPage="home" 
              />
            </h3>
            <p className="text-gray-300 text-sm mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="newsletter_subtitle" 
                value="Get exclusive access to designer collections and private sales" 
                onNavigate={handleNavigateAfterEdit} 
                navPage="home" 
              />
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input 
                type="email" 
                placeholder={p.storefrontText?.email_placeholder || "Your email address"} 
                className="flex-1 px-5 py-3 bg-white/10 border border-white/20 rounded-full text-white placeholder-white/30 focus:border-[#d4af37] outline-none transition" 
              />
              <button 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!(p.previewEditMode && p.activeEditField)) {
                    // Handle newsletter subscription
                  }
                }} 
                className="px-6 py-3 rounded-full bg-[#d4af37] text-white font-semibold hover:bg-white hover:text-[#1a1a2e] transition"
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field="subscribe_button" 
                  value="Subscribe" 
                  onNavigate={handleNavigateAfterEdit} 
                  navPage="home" 
                />
              </button>
            </div>
          </div>
        </div>
        
        <StoreFooter p={p} />
      </div>
      
      {/* Product Modal - NO ADD TO CART BUTTON */}
      {selectedProduct && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-50 flex items-center justify-center p-4 animate-fade-in" onClick={() => setSelectedProduct(null)}>
          <div className="bg-white rounded-2xl max-w-md w-full overflow-hidden shadow-2xl animate-slide-up" onClick={e => e.stopPropagation()}>
            <div className="relative h-80">
              {selectedProduct.productImage ? (
                <img src={AWS_URL + selectedProduct.productImage} alt={selectedProduct.productName} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gray-100 flex items-center justify-center text-6xl">👗</div>
              )}
              <button onClick={() => setSelectedProduct(null)} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/50 text-white hover:bg-[#d4af37] transition">✕</button>
            </div>
            <div className="p-6">
              <h3 className="text-xl font-semibold text-gray-800">{selectedProduct.productName}</h3>
              <p className="text-[#d4af37] text-2xl font-bold mt-2">₹{selectedProduct.salePrice}</p>
              
              {/* Product Description - NO ADD TO CART */}
              <p className="text-gray-500 text-sm mt-3 leading-relaxed">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="product_modal_description" 
                  value={`Exquisite ${selectedProduct.productName} from our luxury collection. Handcrafted with precision and care.`}
                  onNavigate={handleNavigateAfterEdit} 
                  navPage="collection" 
                  isInModal={true} 
                />
              </p>
              
              <div className="mt-4 text-sm text-[#d4af37] text-center bg-[#d4af37]/10 p-3 rounded-full border border-[#d4af37]/20">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="product_modal_message" 
                  value="✨ Luxury craftsmanship at its finest"
                  onNavigate={handleNavigateAfterEdit} 
                  navPage="collection" 
                  isInModal={true} 
                />
              </div>
              
              <button 
                onClick={() => setSelectedProduct(null)} 
                className="w-full mt-4 py-3 bg-[#d4af37] text-white rounded-full font-semibold hover:bg-[#1a1a2e] transition"
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field="close_product_button" 
                  value="Close" 
                  onNavigate={handleNavigateAfterEdit} 
                  navPage="collection" 
                  isInModal={true} 
                />
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Shoes Modal - NO ADD TO CART BUTTON */}
      {selectedShoe && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-50 flex items-center justify-center p-4 animate-fade-in" onClick={() => setSelectedShoe(null)}>
          <div className="bg-white rounded-2xl max-w-md w-full overflow-hidden shadow-2xl animate-slide-up" onClick={e => e.stopPropagation()}>
            <div className="relative h-80">
              <InlineEditableImage p={p} field={selectedShoe.imageField} className="block w-full h-full overflow-hidden">
                <img src={selectedShoe.image} alt={selectedShoe.name} className="w-full h-full object-cover" />
              </InlineEditableImage>
              <button onClick={() => setSelectedShoe(null)} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/50 text-white hover:bg-[#d4af37] transition">✕</button>
            </div>
            <div className="p-6">
              <h3 className="text-xl font-semibold text-gray-800">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedShoe.nameKey} 
                  value={selectedShoe.name} 
                  onNavigate={handleNavigateAfterEdit} 
                  navPage="shoes" 
                  isInModal={true} 
                />
              </h3>
              <p className="text-gray-500 text-sm mt-1">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedShoe.descriptionKey} 
                  value={selectedShoe.description} 
                  onNavigate={handleNavigateAfterEdit} 
                  navPage="shoes" 
                  isInModal={true} 
                />
              </p>
              <div className="mt-3 flex items-center gap-2">
                <span className="text-2xl font-bold text-[#d4af37]">
                  ₹<InlineEditableTextWithNav 
                    p={p} 
                    field={selectedShoe.priceKey} 
                    value={selectedShoe.price.toString()} 
                    onNavigate={handleNavigateAfterEdit} 
                    navPage="shoes" 
                    isInModal={true} 
                  />
                </span>
                <span className="text-sm text-gray-400 line-through">
                  ₹<InlineEditableTextWithNav 
                    p={p} 
                    field={selectedShoe.originalPriceKey} 
                    value={selectedShoe.originalPrice.toString()} 
                    onNavigate={handleNavigateAfterEdit} 
                    navPage="shoes" 
                    isInModal={true} 
                  />
                </span>
                <span className="text-xs text-green-600 ml-2">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={selectedShoe.discountKey} 
                    value={`Save ${selectedShoe.discount}%`} 
                    onNavigate={handleNavigateAfterEdit} 
                    navPage="shoes" 
                    isInModal={true} 
                  />
                </span>
              </div>
              
              <div className="mt-4 text-sm text-[#d4af37] text-center bg-[#d4af37]/10 p-3 rounded-full border border-[#d4af37]/20">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="shoe_modal_message" 
                  value="👠 Step into luxury with our designer footwear"
                  onNavigate={handleNavigateAfterEdit} 
                  navPage="shoes" 
                  isInModal={true} 
                />
              </div>
              
              <button 
                onClick={() => setSelectedShoe(null)} 
                className="w-full mt-4 py-3 bg-[#d4af37] text-white rounded-full font-semibold hover:bg-[#1a1a2e] transition"
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field="close_product_button" 
                  value="Close" 
                  onNavigate={handleNavigateAfterEdit} 
                  navPage="shoes" 
                  isInModal={true} 
                />
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Blog Modal */}
      {selectedPost && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div 
            className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" 
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 z-10 bg-white border-b border-gray-100 p-4 flex justify-between items-center rounded-t-3xl">
              <h3 className="text-lg font-semibold text-[#1a1a2e]">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="blog_modal_title" 
                  value="Blog Post" 
                  onNavigate={handleNavigateAfterEdit} 
                  navPage="editorial" 
                  isInModal={true} 
                />
              </h3>
              <button 
                onClick={closeBlogModal} 
                className="w-10 h-10 rounded-full bg-gray-100 text-gray-600 hover:bg-[#d4af37] hover:text-white transition flex items-center justify-center text-xl"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              <div className="mb-6">
                <InlineEditableImage p={p} field={selectedPost.imageField} className="block w-full overflow-hidden">
                  <img src={selectedPost.image} alt="Blog" className="w-full h-64 object-cover rounded-xl" />
                </InlineEditableImage>
              </div>
              
              <h2 className="text-2xl md:text-3xl font-bold text-[#1a1a2e] mb-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedPost.titleKey} 
                  value={selectedPost.title}
                  onNavigate={handleNavigateAfterEdit}
                  navPage="editorial"
                  isInModal={true}
                />
              </h2>
              
              <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-gray-500 pb-4 border-b border-gray-100 mb-4">
                <span>
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={selectedPost.dateKey} 
                    value={selectedPost.date}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="editorial"
                    isInModal={true}
                  />
                </span>
                <span>•</span>
                <span>
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="by_prefix" 
                    value="By"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="editorial"
                    isInModal={true}
                  />{' '}
                  <span className="font-semibold text-[#1a1a2e]">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={selectedPost.authorKey} 
                      value={selectedPost.author}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="editorial"
                      isInModal={true}
                    />
                  </span>
                </span>
              </div>
              
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-600 leading-relaxed whitespace-pre-wrap">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={selectedPost.fullContentKey} 
                    value={selectedPost.fullContent}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="editorial"
                    isInModal={true}
                  />
                </p>
              </div>
              
              <div className="mt-8 pt-4 border-t border-gray-100 flex justify-end">
                <button 
                  onClick={closeBlogModal} 
                  className="px-6 py-2 rounded-full bg-[#d4af37] text-white hover:bg-[#1a1a2e] transition"
                >
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="close_button" 
                    value="Close"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="editorial"
                    isInModal={true}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <style jsx>{`
        @keyframes shimmer-slow {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-shimmer-slow {
          animation: shimmer-slow 4s infinite;
        }
        @keyframes marquee { 0% { transform: translateX(100%); } 100% { transform: translateX(-100%); } }
        .animate-marquee { animation: marquee 20s linear infinite; display: inline-block; }
        @keyframes float { 0%,100% { transform: translateY(0px); } 50% { transform: translateY(-15px); } }
        .animate-float { animation: float 6s ease-in-out infinite; }
        .animate-float-delayed { animation: float 6s ease-in-out infinite 3s; }
        @keyframes scroll { 0% { transform: translateY(0); opacity: 1; } 100% { transform: translateY(10px); opacity: 0; } }
        .animate-scroll { animation: scroll 1.5s ease-in-out infinite; }
        @keyframes slide-up { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
        .animate-slide-up { animation: slide-up 0.6s ease-out forwards; opacity: 0; animation-fill-mode: forwards; }
        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
        .animate-fade-in { animation: fade-in 0.5s ease-out forwards; opacity: 0; animation-fill-mode: forwards; }
        @keyframes bounce-in { 0% { opacity: 0; transform: scale(0.8); } 60% { transform: scale(1.05); } 100% { opacity: 1; transform: scale(1); } }
        .animate-bounce-in { animation: bounce-in 0.5s ease-out forwards; opacity: 0; animation-fill-mode: forwards; }
        @keyframes bounce-slow { 0%,100% { transform: translateX(-50%) translateY(0); } 50% { transform: translateX(-50%) translateY(-10px); } }
        .animate-bounce-slow { animation: bounce-slow 2s ease-in-out infinite; }
        .delay-100 { animation-delay: 0.1s; animation-fill-mode: forwards; }
        .delay-200 { animation-delay: 0.2s; animation-fill-mode: forwards; }
        .delay-300 { animation-delay: 0.3s; animation-fill-mode: forwards; }
        .line-clamp-1 { display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden; }
      `}</style>
    </div>
  );
}